import { Shield, Heart, Target, Leaf, SearchCheck, FileCheck, Handshake, Anchor } from 'lucide-react'

export function WhoWeAreView() {
  const values = [
    { icon: Shield, title: 'Trust', text: 'We only list producers and logistics partners we have verified ourselves — through documentation audits, site visits and continuous shipment scoring.' },
    { icon: Target, title: 'Precision', text: 'Every order is tracked from contract to delivery with clear documentation, confirmed specs and a single accountable owner on our side.' },
    { icon: Heart, title: 'Partnership', text: 'We act as an extension of your procurement team — advising on spec, timing and market movement, not just taking orders.' },
    { icon: Leaf, title: 'Sustainability', text: 'Where possible we prioritise sustainable farming, low-mileage routing and recyclable packaging across our supply network.' }
  ]

  const departments = [
    {
      icon: SearchCheck,
      title: 'Sourcing & Procurement',
      text: 'Travels to origin regions, audits producers and negotiates direct supply agreements — cutting out stacked intermediaries.'
    },
    {
      icon: FileCheck,
      title: 'Quality & Compliance',
      text: 'Reviews certificates, books third-party inspections and makes sure every shipment matches the agreed specification.'
    },
    {
      icon: Anchor,
      title: 'Logistics & Freight',
      text: 'Books ocean, road and multimodal freight, manages customs documents and tracks cargo until it is signed for.'
    },
    {
      icon: Handshake,
      title: 'Trade Support',
      text: 'Your single point of contact for quotes, order status, claims and re-orders — multilingual and region-aware.'
    }
  ]

  const vetting = [
    'Documentation audit — registration, export licences, food-safety certification',
    'Site or desk audit — production capacity, hygiene, storage and traceability',
    'Sample verification — product tested against your spec before any contract',
    'Ongoing scoring — delivery accuracy and claim history reviewed every shipment'
  ]

  return (
    <main>
      <section className="relative h-80 bg-brand-navy px-4 sm:px-6 lg:px-8">
        <img
          src="https://images.unsplash.com/photo-1500937386664-56d1dfef3854?auto=format&fit=crop&w=1600&q=80"
          alt="Trade partners shaking hands in a wheat field"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col items-start justify-center text-white">
          <span className="mb-3 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-gold-light">
            Our Identity
          </span>
          <h1 className="font-display text-4xl font-bold sm:text-5xl">Who we are</h1>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-2">
          <div>
            <h2 className="heading-accent font-display text-2xl font-bold text-brand-ink">A team built on trade</h2>
            <p className="mt-6 leading-relaxed text-brand-slate">
              Alpha Import Export was founded by a group of supply-chain, commodity and international-trade professionals
              who saw how difficult it was for buyers to find consistent, compliant partners across metals, chemicals,
              food and agricultural products — and decided to build that partner themselves.
            </p>
            <p className="mt-4 leading-relaxed text-brand-slate">
              Today, at Alpha Import Export, we combine direct sourcing relationships, digital procurement tools and
              hands-on logistics know-how to make global buying simpler — whether you are a single store ordering cases
              or a manufacturer booking containers.
            </p>
            <p className="mt-4 leading-relaxed text-brand-slate">
              We are traders, not brokers. Every product we quote is one we can source, document and ship — and every
              buyer we serve has a named contact at our office in Bree, Belgium.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            {values.map((v) => (
              <div key={v.title} className="rounded-2xl border border-brand-gold/10 bg-white p-6 shadow-card">
                <v.icon className="h-8 w-8 text-brand-gold" />
                <h3 className="mt-4 font-display text-lg font-semibold text-brand-ink">{v.title}</h3>
                <p className="mt-2 text-sm text-brand-slate">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-4 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-4 sm:grid-cols-3">
          {[
            { img: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=800&q=80', alt: 'Aerial view of a container port' },
            { img: 'https://images.unsplash.com/photo-1516937941344-00b4e0337589?auto=format&fit=crop&w=800&q=80', alt: 'Industrial refinery complex' },
            { img: 'https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=800&q=80', alt: 'Warehouse aisle stocked with goods' },
          ].map((im) => (
            <div key={im.img} className="overflow-hidden rounded-2xl shadow-card">
              <img src={im.img} alt={im.alt} loading="lazy" className="h-52 w-full object-cover transition-transform duration-500 hover:scale-105 sm:h-64" />
            </div>
          ))}
        </div>
      </section>

      <section className="bg-brand-shell px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <h2 className="heading-accent font-display text-2xl font-bold text-brand-ink sm:text-3xl">
            The teams behind your orders
          </h2>
          <p className="mt-3 max-w-2xl text-brand-slate">
            Four specialist functions at Alpha Import Export work on every order — from the farm gate to your receiving dock.
          </p>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {departments.map((d) => (
              <div key={d.title} className="card-lift rounded-2xl bg-white p-6 shadow-card">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-navy">
                  <d.icon className="h-6 w-6 text-brand-gold" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-brand-ink">{d.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-slate">{d.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl shadow-card">
            <img
              src="https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=1000&q=80"
              alt="Producer inspection at origin"
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <h2 className="heading-accent font-display text-2xl font-bold text-brand-ink sm:text-3xl">
              How we vet our partners
            </h2>
            <p className="mt-4 text-brand-slate">
              At Alpha Import Export, every producer and logistics partner passes a four-stage check before their
              first shipment — and is re-scored continuously after that.
            </p>
            <ul className="mt-6 space-y-4">
              {vetting.map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-gold text-sm font-bold text-brand-navy">
                    {i + 1}
                  </span>
                  <p className="pt-1 text-sm leading-relaxed text-brand-slate">{step}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </main>
  )
}
